import { parseISO, format, startOfWeek, endOfWeek, addDays } from "date-fns";

export type Granularity = "day" | "week" | "month";

const WEEK_OPTIONS = { weekStartsOn: 0 } as const;

function getBucketKey(dateStr: string, granularity: Granularity): string {
  if (granularity === "day") return dateStr;
  const d = parseISO(dateStr);
  if (granularity === "week") {
    return format(startOfWeek(d, WEEK_OPTIONS), "yyyy-MM-dd");
  }
  return format(d, "yyyy-MM");
}

function getWeekLabel(dateStr: string): string {
  const d = parseISO(dateStr);
  const start = startOfWeek(d, WEEK_OPTIONS);
  const end = endOfWeek(d, WEEK_OPTIONS);
  const crossMonth = start.getMonth() !== end.getMonth();
  const startFmt = crossMonth ? format(start, "dd/MM") : format(start, "dd");
  return `${startFmt} a ${format(end, "dd/MM/yyyy")}`;
}

function getLabel(dateStr: string, granularity: Granularity): string {
  if (granularity === "day") return format(parseISO(dateStr), "dd/MM/yyyy");
  if (granularity === "week") return getWeekLabel(dateStr);
  return format(parseISO(dateStr), "MM/yyyy");
}

function getCurrentBucketKey(granularity: Granularity): string {
  const today = format(new Date(), "yyyy-MM-dd");
  return getBucketKey(today, granularity);
}

function fillDateRange(data: any[], startDate: Date, endDate: Date): any[] {
  const byDate = new Map<string, any>();
  for (const item of data) {
    byDate.set(item.date, item);
  }

  const filled: any[] = [];
  let current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  while (current <= end) {
    const dateStr = format(current, "yyyy-MM-dd");
    filled.push(byDate.get(dateStr) ?? { date: dateStr });
    current = addDays(current, 1);
  }

  return filled;
}

function groupByBucket(data: any[], granularity: Granularity): Map<string, { items: any[]; label: string; isCurrent: boolean }> {
  const currentKey = getCurrentBucketKey(granularity);
  const buckets = new Map<string, { items: any[]; label: string; isCurrent: boolean }>();
  for (const item of data) {
    const key = getBucketKey(item.date, granularity);
    if (!buckets.has(key)) {
      buckets.set(key, { items: [], label: getLabel(item.date, granularity), isCurrent: key === currentKey });
    }
    buckets.get(key)!.items.push(item);
  }
  return buckets;
}

export function aggregateNotificationStats(data: any[] | null, granularity: Granularity, startDate?: Date, endDate?: Date): any[] {
  if (!data || data.length === 0) return [];

  const filledData = startDate && endDate ? fillDateRange(data, startDate, endDate) : data;
  const buckets = groupByBucket(filledData, granularity);

  return Array.from(buckets.values()).map(({ items, label, isCurrent }) => {
    const emailNotifs = items.reduce((acc, x) => acc + (x.email?.notifications ?? 0), 0);
    const emailOpens = items.reduce((acc, x) => acc + (x.email?.opens ?? 0), 0);
    const emailProd = items.reduce((acc, x) => acc + (x.email?.productReviews ?? 0), 0);
    const emailStore = items.reduce((acc, x) => acc + (x.email?.storeReviews ?? 0), 0);
    const emailSeller = items.reduce((acc, x) => acc + (x.email?.sellerReviews ?? 0), 0);

    const waNotifs = items.reduce((acc, x) => acc + (x.whatsapp?.notifications ?? 0), 0);
    const waProd = items.reduce((acc, x) => acc + (x.whatsapp?.productReviews ?? 0), 0);
    const waStore = items.reduce((acc, x) => acc + (x.whatsapp?.storeReviews ?? 0), 0);
    const waSeller = items.reduce((acc, x) => acc + (x.whatsapp?.sellerReviews ?? 0), 0);

    const uniqueOrders = items.reduce((acc, x) => acc + (x.total?.uniqueOrders ?? 0), 0);
    const totalReviews = emailProd + emailStore + emailSeller + waProd + waStore + waSeller;

    return {
      date: label,
      _isCurrent: isCurrent,
      email: {
        notifications: emailNotifs,
        opens: emailOpens,
        openRate: emailNotifs > 0 ? emailOpens / emailNotifs : 0,
        productReviews: emailProd,
        storeReviews: emailStore,
        sellerReviews: emailSeller,
        conversionRate: emailNotifs > 0 ? (emailProd + emailStore + emailSeller) / emailNotifs : 0,
      },
      whatsapp: {
        notifications: waNotifs,
        productReviews: waProd,
        storeReviews: waStore,
        sellerReviews: waSeller,
        conversionRate: waNotifs > 0 ? (waProd + waStore + waSeller) / waNotifs : 0,
      },
      total: {
        uniqueOrders,
        reviews: totalReviews,
        effectivenessRate: uniqueOrders > 0 ? totalReviews / uniqueOrders : 0,
      },
    };
  });
}

export interface EffectivenessSummary {
  uniqueOrders: number;
  reviews: number;
  effectivenessRate: number;
}

export function computeEffectivenessSummary(data: any[] | null): EffectivenessSummary {
  if (!data || data.length === 0) return { uniqueOrders: 0, reviews: 0, effectivenessRate: 0 };

  const uniqueOrders = data.reduce((acc, x) => acc + (x.total?.uniqueOrders ?? 0), 0);
  const reviews = data.reduce(
    (acc, x) =>
      acc + (x.total?.productReviews ?? 0) + (x.total?.storeReviews ?? 0) + (x.total?.sellerReviews ?? 0),
    0
  );

  return {
    uniqueOrders,
    reviews,
    effectivenessRate: uniqueOrders > 0 ? reviews / uniqueOrders : 0,
  };
}

export function aggregateNpsStats(data: any[] | null, granularity: Granularity): any[] {
  if (!data || data.length === 0) return [];

  const buckets = groupByBucket(data, granularity);

  return Array.from(buckets.values()).map(({ items, label, isCurrent }) => {
    const emailNotifs = items.reduce((acc, x) => acc + (x.email?.notifications ?? 0), 0);
    const emailResponses = items.reduce((acc, x) => acc + (x.email?.responses ?? 0), 0);

    const waNotifs = items.reduce((acc, x) => acc + (x.whatsapp?.notifications ?? 0), 0);
    const waResponses = items.reduce((acc, x) => acc + (x.whatsapp?.responses ?? 0), 0);

    return {
      date: label,
      _isCurrent: isCurrent,
      email: {
        notifications: emailNotifs,
        responses: emailResponses,
        conversionRate: emailNotifs > 0 ? emailResponses / emailNotifs : 0,
      },
      whatsapp: {
        notifications: waNotifs,
        responses: waResponses,
        conversionRate: waNotifs > 0 ? waResponses / waNotifs : 0,
      },
    };
  });
}
